import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CircularProgress, MenuItem, Select, Tooltip } from "@mui/material";
import { useBranches } from "hooks/useBranches";
import { MdInfoOutline, MdWarningAmber } from "react-icons/md";
import { useCloseReasonsStats } from "hooks/useCloseReasonsStats";
import { CloseReasonItem, CloseReasonKey } from "interfaces/entities/ticketStats";

// Цвета: «здоровое» закрытие — зелёное, «тревожные» сигналы — красный/оранжевый,
// технические причины — серые. Это даёт визуальный сигнал «всё ок / не ок» с одного взгляда.
const REASON_STYLE: Record<string, { label: string; color: string; description: string }> = {
  SOLVED: {
    label: "Решено",
    color: "#22C55E",
    description: "Сотрудник решил вопрос, тикет закрыт штатно.",
  },
  NO_RESPONSE: {
    label: "Нет ответа",
    color: "#EF4444",
    description:
      "Авто-закрытие после периода ожидания. Клиент не подтвердил решение — возможно, мы закрыли «в пустоту».",
  },
  DUPLICATE: {
    label: "Дубликат",
    color: "#F59E0B",
    description:
      "Повторное обращение по тому же вопросу. Высокая доля = слабое самообслуживание / поиск.",
  },
  WONT_FIX: {
    label: "Не будет решено",
    color: "#94A3B8",
    description: "Сознательно отказались решать (вне scope, политика и т.п.).",
  },
  CANCELLED: {
    label: "Отозвано клиентом",
    color: "#CBD5E1",
    description: "Клиент сам отозвал обращение.",
  },
  SPAM: {
    label: "Спам",
    color: "#64748B",
    description: "Шум на входе. Если доля растёт — стоит фильтровать раньше.",
  },
  UNSET: {
    label: "Без причины",
    color: "#E2E8F0",
    description: "Старые тикеты, закрытые до того как причина стала обязательной.",
  },
};

const styleFor = (reason: CloseReasonKey) => REASON_STYLE[reason || "UNSET"] || REASON_STYLE.UNSET;

const WINDOW_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 7, label: "7 дней" },
  { value: 30, label: "30 дней" },
  { value: 90, label: "90 дней" },
];

// Пороги, выше которых поднимаем «тревогу» в callout-блоке.
// Значения выбраны эмпирически: NO_RESPONSE > 20% — клиенты не подтверждают;
// DUPLICATE > 15% — много повторных обращений (проблема самообслуживания).
const NO_RESPONSE_ALERT_THRESHOLD = 20;
const DUPLICATE_ALERT_THRESHOLD = 15;

const Insight = ({ severity, text }: { severity: "warn" | "info"; text: string }) => {
  const palette =
    severity === "warn"
      ? { bg: "#FEF3C7", color: "#92400E", icon: <MdWarningAmber size={18} /> }
      : { bg: "#E0F2FE", color: "#075985", icon: <MdInfoOutline size={18} /> };
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        background: palette.bg,
        color: palette.color,
        padding: "8px 12px",
        borderRadius: 8,
        fontSize: 12,
        lineHeight: 1.4,
      }}
    >
      <span style={{ marginTop: 1 }}>{palette.icon}</span>
      <span>{text}</span>
    </div>
  );
};

const LegendRow = ({ item }: { item: CloseReasonItem }) => {
  const style = styleFor(item.reason);
  return (
    <Tooltip title={style.description} arrow placement="left">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "10px 1fr auto auto",
          gap: 8,
          alignItems: "center",
          padding: "4px 0",
          borderBottom: "1px dashed #F1F5F9",
          cursor: "default",
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: style.color,
          }}
        />
        <span style={{ fontSize: 13 }}>{style.label}</span>
        <span style={{ fontSize: 12, color: "#64748B", minWidth: 36, textAlign: "right" }}>
          {item.count}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: style.color,
            minWidth: 48,
            textAlign: "right",
          }}
        >
          {item.percent}%
        </span>
      </div>
    </Tooltip>
  );
};

export interface TicketsCloseReasonsBreakdownProps {
  defaultWindowDays?: number;
  branchID?: number | string;
}

/**
 * Виджет «Структура закрытий». Показывает, *как* мы закрываем тикеты
 * (а не «сколько»). Высокий NO_RESPONSE = клиенты не подтверждают решение,
 * высокий DUPLICATE = слабое самообслуживание/поиск.
 */
export const TicketsCloseReasonsBreakdown = ({
  defaultWindowDays = 30,
  branchID,
}: TicketsCloseReasonsBreakdownProps) => {
  const [windowDays, setWindowDays] = useState(defaultWindowDays);
  const [localBranchID, setLocalBranchID] = useState<number | "">("");
  const { data: branches = [] } = useBranches();
  const activeBranchID = localBranchID !== "" ? localBranchID : branchID;

  const { data, isLoading, error } = useCloseReasonsStats({
    windowDays,
    ...(activeBranchID ? { branchID: activeBranchID } : {}),
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<any>(null);

  const items: CloseReasonItem[] = useMemo(() => data?.items || [], [data]);

  const insights = useMemo(() => {
    const out: Array<{ severity: "warn" | "info"; text: string }> = [];
    if (!data || data.totalClosed === 0) return out;
    const noResponse = items.find((i) => i.reason === "NO_RESPONSE");
    const duplicate = items.find((i) => i.reason === "DUPLICATE");
    if (noResponse && noResponse.percent >= NO_RESPONSE_ALERT_THRESHOLD) {
      out.push({
        severity: "warn",
        text: `Высокий NO_RESPONSE: ${noResponse.percent}% — клиенты часто не подтверждают решение. Стоит проверить шаблоны ответов и канал коммуникации.`,
      });
    }
    if (duplicate && duplicate.percent >= DUPLICATE_ALERT_THRESHOLD) {
      out.push({
        severity: "warn",
        text: `Дубликатов много: ${duplicate.percent}% — клиенты не находят ответ сами. Стоит расширить FAQ / самообслуживание.`,
      });
    }
    return out;
  }, [data, items]);

  useEffect(() => {
    const ChartCtor = (window as any).Chart;
    if (!data || !canvasRef.current || !ChartCtor) return;
    if (data.totalClosed === 0) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = new ChartCtor(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: items.map((it) => styleFor(it.reason).label),
        datasets: [
          {
            data: items.map((it) => it.count),
            backgroundColor: items.map((it) => styleFor(it.reason).color),
            borderColor: "#fff",
            borderWidth: 2,
            spacing: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        plugins: {
          legend: { display: false }, // легенду рисуем своим списком — он информативнее
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const it = items[ctx.dataIndex];
                if (!it) return "";
                return ` ${it.count} шт. (${it.percent}%)`;
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, items]);

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Структура закрытий</h4>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
              Как мы закрывали тикеты за последние {windowDays} дн.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Select
              size="small"
              value={localBranchID}
              onChange={(e) => setLocalBranchID(e.target.value as number | "")}
              displayEmpty
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">Все филиалы</MenuItem>
              {branches?.data?.map((b: any) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
            >
              {WINDOW_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {isLoading && !data && (
          <div style={{ textAlign: "center", padding: 48 }}>
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && error && (
          <div
            style={{
              textAlign: "center",
              color: "#94A3B8",
              padding: 32,
            }}
          >
            Не удалось загрузить структуру закрытий
          </div>
        )}

        {data && data.totalClosed === 0 && (
          <div
            style={{
              textAlign: "center",
              color: "#94A3B8",
              padding: 48,
            }}
          >
            За выбранный период нет закрытых тикетов
          </div>
        )}

        {data && data.totalClosed > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(220px, 1fr) minmax(220px, 1.2fr)",
              gap: 24,
              alignItems: "start",
            }}
          >
            <div style={{ position: "relative", height: 240 }}>
              <canvas ref={canvasRef} />
              {/* Центр donut: общее число закрытых */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700 }}>{data.totalClosed}</div>
                <div style={{ fontSize: 11, color: "#64748B" }}>закрыто</div>
              </div>
            </div>

            <div>
              <div style={{ marginBottom: 12 }}>
                {items.map((it) => (
                  <LegendRow key={it.reason || "UNSET"} item={it} />
                ))}
              </div>

              {insights.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {insights.map((it, idx) => (
                    <Insight key={idx} severity={it.severity} text={it.text} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TicketsCloseReasonsBreakdown;
